import React, { useState } from "react";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Modal from "../components/Modal/Modal";
import Input from "../components/Input/Input";
import Textarea from "../components/Textarea/Textarea";
import DatePickerField from "../components/FormikComponents/DatePickerField";
import Button from "../components/Button/Button";
import axiosInstance from "../utils/utils";
import { getOrganizationData } from "../actions/organization";
import { hideModal } from "../redux/modalStore";
import {
  clearResetSelection,
  resetSelection,
} from "../redux/rowSelectionStore";
import styles from "./CreateImprovementProjectModal.module.scss";

const CreateImprovementProjectModal = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { t } = useTranslation();
  const openedModal = useSelector((state) => state.modal.openedModal);

  const sessionToken = localStorage.getItem("session_token");
  const [loading, setLoading] = useState(false);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t("createImprovementProjectModal.validation.nameRequired")),
  });

  const formatDate = (inputDate) => {
    if (!inputDate) return new Date().toISOString().split("T")[0];
    return new Date(inputDate).toISOString().split("T")[0];
  };

  const handleSubmit = async (values, actions) => {
    try {
      setLoading(true);

      await axiosInstance.post(
        `/nocodb/improvements?org_id=${orgId}`,
        [
          {
            name: values.name,
            description: values.description,
            date: formatDate(values.date),
          },
        ],
        { headers: { "session-token": sessionToken } },
      );

      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          true,
          true,
          false,
          false,
          false,
        ),
      );

      dispatch(resetSelection());
      window.setTimeout(() => dispatch(clearResetSelection()), 100);

      actions.resetForm();
      dispatch(hideModal());
    } catch (error) {
      console.error("Error creating improvement project:", error);
    } finally {
      setLoading(false);
      actions.setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{ name: "", description: "", date: "" }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={openedModal?.name === "create_improvement_project_modal"}
    >
      {({ resetForm }) => (
        <Modal
          name="create_improvement_project_modal"
          title={t("createImprovementProjectModal.title")}
          subtitle={t("createImprovementProjectModal.subtitle")}
          width="50vw"
          onClose={() => {
            resetForm();
            dispatch(hideModal());
          }}
        >
          <Form className={styles.content}>
            <div className={styles.fieldGroup}>
              <label htmlFor="name" className={styles.label}>
                {t("createImprovementProjectModal.fields.name")}
              </label>
              <Field
                name="name"
                id="name"
                placeholder={t("createImprovementProjectModal.fields.namePlaceholder")}
                component={Input}
                className={styles.input}
                wrapperClassName={styles.fullWidthWrapper}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="description" className={styles.label}>
                {t("createImprovementProjectModal.fields.description")}
              </label>
              <Field
                name="description"
                id="description"
                component={Textarea}
                minHeight="160px"
                placeholder={t(
                  "createImprovementProjectModal.fields.descriptionPlaceholder",
                )}
                className={styles.textarea}
              />
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="date" className={styles.label}>
                {t("createImprovementProjectModal.fields.date")}
              </label>
              <Field
                name="date"
                component={DatePickerField}
                validateOnBlur={true}
                validateOnChange={false}
                maxDate={null}
              />
            </div>

            <div className={styles.actions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={() => {
                  resetForm();
                  dispatch(hideModal());
                }}
                disabled={loading}
              >
                {t("createImprovementProjectModal.actions.cancel")}
              </button>
              <Button
                text={
                  loading
                    ? t("createImprovementProjectModal.actions.creating")
                    : t("createImprovementProjectModal.actions.create")
                }
                type="submit"
                className={styles.submitButton}
                disabled={loading}
              />
            </div>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default CreateImprovementProjectModal;
